import React, { useState } from 'react';
import { Check, Minus, HelpCircle } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import MFPFAQ from './MFPFAQ';
import BackButton from '../BackButton';

const MutualFundsPricing = () => {
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Define the feature rows with tooltip content
  const featureRows = [
    { 
      label: "Direct mutual fund", 
      type: "check",
      tooltip: "Direct mutual funds don't include distributor commissions in their expense ratio, potentially leading to higher returns over time."
    },
    { 
      label: "Regular mutual fund", 
      type: "check",
      tooltip: "Regular mutual funds include distributor commissions in their expense ratio, making them slightly more expensive than direct funds."
    },
    { 
      label: "No. of investors", 
      type: "text",
      tooltip: "The number of investors that can be included under a single account."
    },
    { 
      label: "Unbiased fund recommendations", 
      type: "text-check",
      tooltip: "Our recommendations are based solely on performance and suitability for your goals, never on commissions or incentives."
    },
    { 
      label: "Personalised portfolio construction", 
      type: "check",
      tooltip: "We create a tailored investment portfolio based on your risk profile, financial goals, and investment horizon."
    },
    { 
      label: "Continuous fund monitoring and exit alerts", 
      type: "check",
      tooltip: "Ongoing monitoring of your fund performance with alerts for significant changes or when action is needed."
    },
    { 
      label: "Comprehensive financial planning & goals", 
      type: "check",
      tooltip: "Comprehensive planning covering retirement, tax efficiency, education funding, and other financial goals."
    },
    { 
      label: "Algorithmic prompts for better investing", 
      type: "check",
      tooltip: "Data-driven insights and recommendations to help optimize your investment decisions."
    },
    { 
      label: "Convenient investing in 2000+ mutual funds", 
      type: "check",
      tooltip: "Our platform provides seamless access to over 2,000 mutual funds across all categories and fund houses."
    },
    { 
      label: "Detailed fund fact sheets with Scripbox opinion", 
      type: "check",
      tooltip: "Detailed analysis of each fund including performance history, risk metrics, and expert insights."
    },
    { 
      label: "Risk Profiling", 
      type: "check",
      tooltip: "Assessment of your risk tolerance to ensure your investments align with your comfort level."
    },
    { 
      label: "Right mix of equity, debt, gold and US equity", 
      type: "check",
      tooltip: "Strategic distribution of investments across different asset classes to optimize returns while managing risk."
    },
    { 
      label: "Right number of funds", 
      type: "check",
      tooltip: "We help maintain the ideal number of funds in your portfolio to ensure diversification without unnecessary overlap."
    },
    { 
      label: "Smart Withdraw algorithm to save tax", 
      type: "check",
      tooltip: "Intelligent withdrawal strategies designed to minimize tax impact when you need to access your investments."
    },
    { 
      label: "Capital gain reports for tax filing", 
      type: "check",
      tooltip: "Automated reports that help simplify your tax filing process by clearly documenting capital gains."
    },
    { 
      label: "Track all investments, insurance & loans", 
      type: "check",
      tooltip: "Consolidated view of all your investments, insurance policies, and loans for comprehensive financial tracking."
    },
    { 
      label: "Transfer fund portfolios to Scripbox", 
      type: "check",
      tooltip: "Seamless process to transfer your existing investments to our platform without disruption."
    },
    { 
      label: "Resident Indian, NRI & OCI account", 
      type: "check",
      tooltip: "We accommodate various investor categories including NRIs, OCIs and resident Indians with appropriate compliance."
    },
    { 
      label: "Customer support available: 8am to 8pm, 7 days a week", 
      type: "check",
      tooltip: "Expert assistance available 12 hours a day, every day of the week."
    },
    { 
      label: "Family accounts with unlimited members", 
      type: "text-check",
      tooltip: "Create and manage multiple accounts for different family members under one dashboard."
    },
    { 
      label: "Minor accounts", 
      type: "text-check",
      tooltip: "Special accounts that can be managed by guardians until the minor reaches adulthood."
    },
    { 
      label: "Family wealth tracking", 
      type: "check",
      tooltip: "Consolidated view of all family members' investments for comprehensive wealth management."
    },
    { 
      label: "Family financial plan", 
      type: "check",
      tooltip: "Holistic planning that considers the financial goals and needs of your entire family."
    },
  ];

  // Define the plan data
  const plans = [
    {
      name: "Regular Plan",
      headline: "₹0",
      subtext: "/month",
      buttonText: "Sign Up",
      isPrimary: false,
      description: "Commission-based model with no direct fees",
      values: [
        false, 
        true,
        "Multiple",
        "Every month", 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true,
        true,
        true,
        true,
        true
      ]
    },
    {
      name: "Direct Plan",
      headline: "₹4k",
      subtext: "/month",
      buttonText: "Schedule Meeting",
      isPrimary: true,
      description: "Lower expense ratios for potentially higher returns",
      values: [
        true, 
        false,
        "Multiple",
        "Every month", 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true, 
        true,
        "Coming soon",
        "Coming soon",
        true,
        true
      ]
    }
  ];

  // Handle tooltip toggle
  const toggleTooltip = (index) => {
    if (activeTooltip === index) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(index);
    }
  };

  return (
    <>
      <Header />
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        {/* Header Section */}
        <div className="text-center mb-8 sm:mb-12 pt-16">
          <BackButton to="/fee" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-900 mb-2 sm:mb-4 pt-12">Choose Your Investment Journey</h2>
          <p className="text-gray-600 text-sm sm:text-base">Select the plan that best suits your investment needs</p>
        </div>

        {/* Pricing Table with horizontal scroll for small/medium screens */}
        <div className="relative mb-8 sm:mb-12 max-w-6xl mx-auto">
          {/* Scroll indicator for small/medium screens */}
          <div className="md:hidden text-xs text-gray-500 text-center mb-2 animate-pulse">
            ← Scroll horizontally to view all details →
          </div>
          
          {/* Table container with horizontal scroll */}
          <div className="overflow-x-auto pb-4">
            {/* Minimum width to ensure proper display on small screens */}
            <div className="min-w-[768px] border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-3 sm:p-4 text-left w-1/3 border border-gray-200">
                      <h3 className="text-base sm:text-lg font-semibold text-blue-900">Features</h3>
                    </th>
                    {plans.map((plan, index) => (
                      <th key={index} className={`p-3 sm:p-4 w-1/3 border border-gray-200 ${plan.isPrimary ? 'bg-blue-50' : ''}`}>
                        <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2 text-blue-900">{plan.name}</h3>
                        <div className="mb-4 sm:mb-6">
                          <span className="text-2xl sm:text-3xl font-bold text-blue-900">{plan.headline}</span>
                          <span className="text-gray-600 text-sm sm:text-base">{plan.subtext}</span>
                        </div>
                        <a
                          href={plan.buttonText === 'Sign Up' ? 'https://app.maxiomwealth.com/login' : '/meeting'}
                          className={`w-full inline-block text-center py-1.5 sm:py-2 px-2 sm:px-4 rounded-md transition-colors mb-6 sm:mb-8 text-sm sm:text-base ${
                            plan.isPrimary 
                              ? 'bg-blue-900 text-white hover:bg-blue-800' 
                              : 'border border-blue-900 text-blue-900 hover:bg-blue-50'
                          }`}
                          target={plan.buttonText === 'Sign Up' ? '_blank' : undefined}
                          rel={plan.buttonText === 'Sign Up' ? 'noopener noreferrer' : undefined}
                        >
                          {plan.buttonText}
                        </a>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {featureRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 sm:p-4 border border-gray-200 text-sm sm:text-base relative">
                        <div className="flex items-center">
                          <button 
                            className="mr-2 text-gray-400 hover:text-blue-600 focus:outline-none"
                            onClick={() => toggleTooltip(rowIndex)}
                            onMouseEnter={() => setActiveTooltip(rowIndex)}
                            onMouseLeave={() => setActiveTooltip(null)}
                            aria-label={`Information about ${row.label}`}
                          >
                            <HelpCircle className="h-4 w-4" />
                          </button>
                          <span>{row.label}</span>
                          
                          {/* Tooltip content - positioned to the left */}
                          {activeTooltip === rowIndex && (
                            <div className="absolute z-10 left-8 -mt-2 w-64 sm:w-72 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-xs sm:text-sm text-gray-700">
                              {row.tooltip}
                            </div>
                          )}
                        </div>
                      </td>
                      {plans.map((plan, planIndex) => (
                        <td 
                          key={planIndex} 
                          className={`p-3 sm:p-4 text-center border border-gray-200 ${plan.isPrimary ? 'bg-blue-50' : ''}`}
                        >
                          {row.type === "check" ? (
                            plan.values[rowIndex] ? 
                              <Check className="text-green-600 h-4 w-4 sm:h-5 sm:w-5 mx-auto" /> : 
                              <Minus className="text-gray-400 h-4 w-4 sm:h-5 sm:w-5 mx-auto" />
                          ) : row.type === "text-check" ? (
                            typeof plan.values[rowIndex] === 'string' ? 
                              <span className="text-sm sm:text-base">{plan.values[rowIndex]}</span> :
                              plan.values[rowIndex] ? 
                                <Check className="text-green-600 h-4 w-4 sm:h-5 sm:w-5 mx-auto" /> : 
                                <Minus className="text-gray-400 h-4 w-4 sm:h-5 sm:w-5 mx-auto" />
                          ) : (
                            <span className="text-sm sm:text-base">{plan.values[rowIndex]}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <td className="p-3 sm:p-4 border border-gray-200"></td>
                    {plans.map((plan, index) => (
                      <td key={index} className={`p-3 sm:p-4 text-center border border-gray-200 ${plan.isPrimary ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}>
                        <div className="text-xs sm:text-sm">{plan.description}</div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Scroll fade indicator on right edge for small screens */}
          <div className="md:hidden absolute top-0 right-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <MFPFAQ />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MutualFundsPricing;